"use strict";
/**
 * The published bands a creative's rates are read against — the ONE place the numbers live.
 *
 * These values were not invented for the UI. They are the house media-buying doctrine that the
 * Meta media-buyer mode already states as its fallback anchors when an account is too cold for
 * `metaAdsGetAccountBenchmarks` to produce a distribution. Before this module they existed only as
 * prose inside two agent prompts, which meant the panel a user reads and the advice the agent gives
 * could drift apart on the next edit. Both now interpolate from here.
 *
 * Three properties make a band safe to render, and each is data rather than an unwritten assumption:
 *
 * - **`kind`** — a `verdict` band may colour a metric; a `reference` band may only STATE its range
 *   beside a neutral metric. Meta's own funnel counts (landing page views, adds to cart, initiated
 *   checkouts) are directional for comparing one ad against another and are not absolute rates, so
 *   every funnel band is `reference`. Marking a creative red for a 3% add-to-cart rate would be
 *   asserting a precision the underlying number does not have.
 * - **`scope`** — a video band is meaningless on a static. The account benchmarks pool every format,
 *   so a static reads structurally lower on every play-derived rate; applying a hook-rate band to one
 *   marks it weak for being a static.
 * - **`currency`** — a CPM band is money and has no single value across markets. A band that names
 *   its currency can be degraded to `reference` when the account trades in another, rather than
 *   quietly grading a EUR account against a USD anchor.
 *
 * Bands are expressed as ASCENDING `edges`: a value below `edges[0].upTo` takes `edges[0]`, and the
 * final edge has no upper bound. Boundary values land in the HIGHER band (`< upTo`, not `<=`), which
 * is stated here because an off-by-one at a band edge is the likeliest silent regression.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CROSS_SURFACE_STEP_THROUGH_KEYS = exports.PURCHASE_CALIBRATED_BAND_KEYS = exports.ADAPTIVE_FLOOR_TARGET_ROWS = exports.CHECKOUT_RATE_CREDIBLE_FLOOR = exports.LANDING_PAGE_VIEW_RATE_CREDIBLE_FLOOR = exports.PURCHASE_MATERIAL_SPEND_SHARE = exports.PURCHASE_MATERIAL_COUNT_SHARE = exports.MIN_IMPRESSIONS_FOR_RATE_VERDICT = exports.AOV_MATERIAL_MULTIPLE = exports.FREQUENCY_FATIGUE_THRESHOLD = exports.CPA_OWN_PAST_SEVERE_MULTIPLE = exports.KILL_CPA_MULTIPLE = exports.SIGNIFICANCE_MIN_GAP = exports.VERDICT_PURCHASES_FLOOR = exports.DIRECTIONAL_PURCHASES_FLOOR = exports.CREATIVE_METRIC_BANDS = exports.ECOM_ANCHORS = exports.VIEW_THROUGH_THRESHOLDS = exports.FUNNEL_THRESHOLDS = exports.VIDEO_RATE_THRESHOLDS = void 0;
exports.bandJudgesOutcome = bandJudgesOutcome;
exports.creativeBand = creativeBand;
exports.creativeBandEdge = creativeBandEdge;
exports.ecomFallbackAnchorSentence = ecomFallbackAnchorSentence;
exports.videoBandSentence = videoBandSentence;
exports.viewThroughShare = viewThroughShare;
exports.viewThroughShareSentence = viewThroughShareSentence;
/** A fraction (0.22) as a whole-percent string ("22"). */
function pct(fraction) {
    return String(Math.round(fraction * 1000) / 10);
}
/**
 * Money in major units for prose: whole values stay whole ("10"), fractional ones carry both
 * decimals ("13.50"). Matches how these anchors have always been written in the media-buying prompt,
 * so interpolating them produces a byte-identical sentence.
 */
function money(major) {
    return Number.isInteger(major) ? String(major) : major.toFixed(2);
}
const E = (upTo, tone, label) => ({ upTo, tone, label });
/**
 * The video-diagnostic thresholds, named once. Both the band `edges` and the prompt sentence read
 * these, so the panel and the agent cannot state different numbers — which is the whole reason this
 * module exists. All are fractions (0.25 = 25%).
 *
 * The doctrine names three points on hook rate (weak / solid / strong) and leaves the span between
 * `solidTo` and `strongFrom` unnamed; the band model fills it as the same tone as `solid`, because a
 * creative at 35% is plainly not weak and calling it nothing would leave a hole in the scale.
 */
exports.VIDEO_RATE_THRESHOLDS = {
    hook: { weakBelow: 0.2, solidFrom: 0.25, solidTo: 0.3, strongFrom: 0.4 },
    hold: { okFrom: 0.25, okTo: 0.4, strongFrom: 0.6 },
    hookToClick: { weakBelow: 0.05, strongFrom: 0.15 },
};
/** Outbound-CTR and funnel step-through thresholds. Fractions. */
exports.FUNNEL_THRESHOLDS = {
    outboundCtr: { okFrom: 0.01, strongFrom: 0.015 },
    landingPageViewRate: { healthyFrom: 0.85 },
    addToCartRate: { typicalFrom: 0.04, typicalTo: 0.08 },
    checkoutCompletion: { leakBelow: 0.5, healthyFrom: 0.65, healthyTo: 0.75 },
};
/** View-through share of credited purchases. Fractions. */
exports.VIEW_THROUGH_THRESHOLDS = { normalFrom: 0.2, normalTo: 0.25, reviewFrom: 0.3, overClaimingFrom: 0.8 };
/**
 * Generic e-commerce anchors, in the currency they were quoted in. Named separately from the bands
 * because the agent's fallback sentence quotes the point estimates, not the bands.
 */
exports.ECOM_ANCHORS = {
    /** Median cost per purchase. */
    cpaMajor: 38,
    cpmMajor: 13.5,
    cpmUsMajor: 10,
    cpmEuMajor: 8.5,
    currency: 'USD',
    ctrFraction: 0.022,
    roasRatio: 1.9,
    /** STORE session conversion rate (orders ÷ sessions) — NOT the ad-level pixel rate. */
    storeSessionCvrFraction: 0.016,
};
exports.CREATIVE_METRIC_BANDS = {
    hookRate: {
        key: 'hookRate',
        metricLabel: 'Hook rate',
        kind: 'verdict',
        scope: 'video',
        currency: null,
        unit: 'fraction',
        edges: [
            E(exports.VIDEO_RATE_THRESHOLDS.hook.weakBelow, 'bad', 'weak'),
            E(exports.VIDEO_RATE_THRESHOLDS.hook.solidFrom, 'warn', 'below solid'),
            E(exports.VIDEO_RATE_THRESHOLDS.hook.strongFrom, 'neutral', 'solid'),
            E(null, 'good', 'strong'),
        ],
        badge: `video cold: <${pct(exports.VIDEO_RATE_THRESHOLDS.hook.weakBelow)}% weak · ${pct(exports.VIDEO_RATE_THRESHOLDS.hook.solidFrom)}–${pct(exports.VIDEO_RATE_THRESHOLDS.hook.solidTo)}% solid · >${pct(exports.VIDEO_RATE_THRESHOLDS.hook.strongFrom)}% strong`,
        grounding: 'House media-buying doctrine; the fallback anchors used when an account is too cold to benchmark.',
    },
    holdRate: {
        key: 'holdRate',
        metricLabel: 'Hold rate',
        kind: 'verdict',
        scope: 'video',
        currency: null,
        unit: 'fraction',
        edges: [
            E(exports.VIDEO_RATE_THRESHOLDS.hold.okFrom, 'warn', 'below the ok band'),
            E(exports.VIDEO_RATE_THRESHOLDS.hold.strongFrom, 'neutral', 'ok'),
            E(null, 'good', 'strong'),
        ],
        badge: `ok: ${pct(exports.VIDEO_RATE_THRESHOLDS.hold.okFrom)}–${pct(exports.VIDEO_RATE_THRESHOLDS.hold.okTo)}% · strong: >${pct(exports.VIDEO_RATE_THRESHOLDS.hold.strongFrom)}%`,
        grounding: 'House media-buying doctrine, measured against ThruPlays over three-second plays.',
    },
    hookToClick: {
        key: 'hookToClick',
        metricLabel: 'Hook-to-click',
        kind: 'verdict',
        scope: 'video',
        currency: null,
        unit: 'fraction',
        edges: [
            E(exports.VIDEO_RATE_THRESHOLDS.hookToClick.weakBelow, 'bad', 'weak'),
            E(exports.VIDEO_RATE_THRESHOLDS.hookToClick.strongFrom, 'neutral', 'ok'),
            E(null, 'good', 'strong'),
        ],
        badge: `<${pct(exports.VIDEO_RATE_THRESHOLDS.hookToClick.weakBelow)}% weak · >${pct(exports.VIDEO_RATE_THRESHOLDS.hookToClick.strongFrom)}% strong`,
        grounding: 'House media-buying doctrine; a high hook with a low hook-to-click is an offer problem.',
    },
    outboundCtr: {
        key: 'outboundCtr',
        metricLabel: 'Outbound CTR',
        kind: 'reference',
        scope: 'any',
        currency: null,
        unit: 'fraction',
        edges: [
            E(exports.FUNNEL_THRESHOLDS.outboundCtr.okFrom, 'neutral', 'below ok'),
            E(exports.FUNNEL_THRESHOLDS.outboundCtr.strongFrom, 'neutral', 'ok'),
            E(null, 'neutral', 'strong'),
        ],
        badge: `>${pct(exports.FUNNEL_THRESHOLDS.outboundCtr.okFrom)}% ok · >${pct(exports.FUNNEL_THRESHOLDS.outboundCtr.strongFrom)}% strong`,
        grounding: 'Generic e-commerce anchor. Outbound CTR varies widely by placement mix, so it states a range rather than judging.',
    },
    landingPageViewRate: {
        key: 'landingPageViewRate',
        metricLabel: 'Landing page views / clicks',
        kind: 'reference',
        scope: 'any',
        currency: null,
        unit: 'fraction',
        edges: [E(exports.FUNNEL_THRESHOLDS.landingPageViewRate.healthyFrom, 'neutral', 'below healthy'), E(null, 'neutral', 'healthy')],
        badge: `≥ ${pct(exports.FUNNEL_THRESHOLDS.landingPageViewRate.healthyFrom)}% healthy`,
        grounding: "Meta's own funnel counts are directional for ad-to-ad comparison, never absolute rates.",
    },
    addToCartRate: {
        key: 'addToCartRate',
        metricLabel: 'Adds to cart / landing page views',
        kind: 'reference',
        scope: 'any',
        currency: null,
        unit: 'fraction',
        edges: [
            E(exports.FUNNEL_THRESHOLDS.addToCartRate.typicalFrom, 'neutral', 'below typical'),
            E(exports.FUNNEL_THRESHOLDS.addToCartRate.typicalTo, 'neutral', 'typical'),
            E(null, 'neutral', 'above typical'),
        ],
        badge: `${pct(exports.FUNNEL_THRESHOLDS.addToCartRate.typicalFrom)}–${pct(exports.FUNNEL_THRESHOLDS.addToCartRate.typicalTo)}% typical`,
        grounding: "Meta's own funnel counts are directional for ad-to-ad comparison, never absolute rates.",
    },
    checkoutCompletionRate: {
        key: 'checkoutCompletionRate',
        metricLabel: 'Purchases / checkouts',
        // The one funnel band allowed a verdict: a purchase-to-checkout gap is a finding about the
        // DESTINATION (a broken or costly checkout), not a benchmark comparison against other ads.
        kind: 'verdict',
        scope: 'any',
        currency: null,
        unit: 'fraction',
        edges: [
            E(exports.FUNNEL_THRESHOLDS.checkoutCompletion.leakBelow, 'warn', 'checkout leak?'),
            E(exports.FUNNEL_THRESHOLDS.checkoutCompletion.healthyFrom, 'neutral', 'below healthy'),
            E(null, 'neutral', 'healthy'),
        ],
        badge: `healthy: ${pct(exports.FUNNEL_THRESHOLDS.checkoutCompletion.healthyFrom)}–${pct(exports.FUNNEL_THRESHOLDS.checkoutCompletion.healthyTo)}%`,
        grounding: 'A completion far below the healthy range points at the checkout rather than the creative.',
    },
    cpm: {
        key: 'cpm',
        metricLabel: 'CPM',
        kind: 'reference',
        scope: 'any',
        currency: exports.ECOM_ANCHORS.currency,
        unit: 'major',
        edges: [E(exports.ECOM_ANCHORS.cpmMajor, 'neutral', 'below the e-com median'), E(null, 'neutral', 'above the e-com median')],
        badge: `e-com median ~${money(exports.ECOM_ANCHORS.cpmMajor)} ${exports.ECOM_ANCHORS.currency}`,
        grounding: `Generic e-commerce anchor in ${exports.ECOM_ANCHORS.currency} (US ~${money(exports.ECOM_ANCHORS.cpmUsMajor)}, EU ~${money(exports.ECOM_ANCHORS.cpmEuMajor)}); CPM is money, so it is reference context outside its own currency.`,
    },
    ctr: {
        key: 'ctr',
        metricLabel: 'CTR',
        kind: 'reference',
        scope: 'any',
        currency: null,
        unit: 'fraction',
        edges: [E(exports.ECOM_ANCHORS.ctrFraction, 'neutral', 'below the e-com median'), E(null, 'neutral', 'above the e-com median')],
        badge: `e-com median ~${pct(exports.ECOM_ANCHORS.ctrFraction)}%`,
        grounding: 'Generic e-commerce anchor; blends every placement and objective.',
    },
    roas: {
        key: 'roas',
        metricLabel: 'ROAS',
        // Reference, not verdict: the creative-performance surface already carries its OWN absolute
        // ROAS verdict, keyed on break-even at a round contribution margin (`roasBand` in the web
        // feature). This band exists to state the market median beside that verdict, not to compete
        // with it — two ROAS verdicts on one row would be a contradiction waiting to happen.
        kind: 'reference',
        scope: 'any',
        currency: null,
        unit: 'ratio',
        edges: [E(exports.ECOM_ANCHORS.roasRatio, 'neutral', 'below the e-com median'), E(null, 'neutral', 'above the e-com median')],
        badge: `e-com median ~${exports.ECOM_ANCHORS.roasRatio}x`,
        grounding: 'Generic e-commerce anchor; a break-even target is per-brand and comes from contribution margin.',
    },
    viewThroughShare: {
        key: 'viewThroughShare',
        metricLabel: 'View-through share',
        kind: 'verdict',
        scope: 'any',
        currency: null,
        unit: 'fraction',
        edges: [
            E(exports.VIEW_THROUGH_THRESHOLDS.reviewFrom, 'neutral', 'normal'),
            E(exports.VIEW_THROUGH_THRESHOLDS.overClaimingFrom, 'warn', 'worth review'),
            E(null, 'bad', 'platform over-claiming'),
        ],
        badge: `normal: ${pct(exports.VIEW_THROUGH_THRESHOLDS.normalFrom)}–${pct(exports.VIEW_THROUGH_THRESHOLDS.normalTo)}%`,
        grounding: 'House doctrine on view-heavy accounts: >30% is worth review, >80% means the platform is claiming sales it did not drive.',
    },
};
/**
 * Purchases required before a purchase-derived figure stops being labelled **directional**.
 *
 * This is NOT the same floor as the one that decides whether a metric chip may be tinted at all.
 * The two are nested and deliberately different:
 *
 * - **3 purchases** (`VERDICT_PURCHASES_FLOOR`, mirroring `SEGMENT_MIN_CONVERSIONS` in the API's
 *   `media-buying-gates.ts`) is the bar to TINT a chip. Below it there is no verdict at all, because
 *   a green chip on a 3.5x off one sale invites acting on noise.
 * - **5 purchases** (`DIRECTIONAL_PURCHASES_FLOOR`) is the bar to stop CALLING THE BLOCK
 *   DIRECTIONAL. Between 3 and 4 a verdict may show, and the figures are still labelled directional
 *   so the reader knows the sample is thin.
 *
 * Both floors apply together; neither replaces the other.
 */
exports.DIRECTIONAL_PURCHASES_FLOOR = 5;
/**
 * The floor below which no purchase-derived figure carries a verdict. See the note above.
 *
 * This is the ONE definition — the web grid's `MIN_PURCHASES_FOR_TONE` re-exports it rather than
 * restating `3`, so the panel's chip tone and the grid card's chip tone cannot drift.
 */
exports.VERDICT_PURCHASES_FLOOR = 3;
/**
 * The relative gap below which two figures are NOT called different.
 *
 * The house rule for calling a winner is a 20% relative gap; anything tighter is "too close to call
 * — keep both running". The same number is what makes a SELF-REFERENTIAL move (a creative against
 * its own previous window, or against the brand's target) worth a verdict: a CPA 6% above target is
 * the same account on a different week, and colouring it red teaches the reader to distrust the
 * colour. Below this gap the figure reads as parity.
 *
 * The API's `media-buying-gates.ts` re-exports this rather than restating `0.2`, so the gate the
 * agent enforces and the tone a cell carries cannot drift apart.
 */
exports.SIGNIFICANCE_MIN_GAP = 0.2;
/**
 * CPA at or above this multiple of the brand's in-force target is at the kill threshold.
 *
 * From the optimization doctrine's kill criteria — "CPA materially above target (e.g. >~1.5-2x)" —
 * and the lower edge of that range is used, because a cell's tone is a prompt to look rather than a
 * pause instruction.
 */
exports.KILL_CPA_MULTIPLE = 1.5;
/**
 * CPA at or above this multiple of the SAME creative's immediately-preceding window.
 *
 * A doubling against its own past is the point at which a cost trend stops being drift. It is a
 * fatigue SUPPORTING signal, not a fatigue diagnosis: the doctrine's fatigue call needs rising
 * frequency in the same audience, falling first-time impressions and an unbroadened audience, none
 * of which a single row carries. The tone therefore judges the number, not its cause.
 */
exports.CPA_OWN_PAST_SEVERE_MULTIPLE = 2;
/**
 * Frequency above which repetition becomes a fatigue SUPPORTING signal — but only alongside a
 * falling outbound CTR in the same window.
 *
 * Neither a high nor a low frequency is good or bad on its own (a stable 5 on a warm retargeting
 * audience is fine), so this threshold never produces a verdict alone: the metric registry flags
 * frequency `warn` exactly when it exceeds this AND the creative's own CTR is declining — the
 * pairing the fatigue doctrine names. From the registry's per-metric spec ("flag > 2.5 when CTR is
 * falling").
 */
exports.FREQUENCY_FATIGUE_THRESHOLD = 2.5;
/**
 * How far above the account's own average an AOV must sit before the basket is called the
 * explanation — and before the soft `good` tint fires.
 *
 * A bare `>` would fire on roughly half of every account's creatives by construction. The registry's
 * "colour softly" note wants a MATERIAL gap: enough that the basket, not the creative, may drive
 * the return. Shared so the table column and the detail panel's Outcome row state the same multiple.
 */
exports.AOV_MATERIAL_MULTIPLE = 1.2;
/**
 * Impressions required before a DELIVERY-derived rate carries a verdict.
 *
 * A purchase floor is the wrong instrument for hook rate: hook rate has nothing to do with purchases,
 * and gating it on them would withhold a verdict from a perfectly well-measured top-of-funnel read.
 * What a play-derived rate needs is enough delivery for the ratio to be stable — six three-second
 * plays out of forty impressions is a 15% hook rate that means nothing, and a red chip on it invites
 * killing a creative that was never tested.
 *
 * Mirrors `MIN_IMPRESSIONS_FOR_BASELINE` in the API's `meta-account-baseline-service.ts`, which is
 * the bar that file already applies before an ad may enter an account's rate distribution.
 */
exports.MIN_IMPRESSIONS_FOR_RATE_VERDICT = 1000;
/**
 * How much of an account's conversion volume must be purchases before the purchase is treated as its
 * outcome — as a share of the account's HIGHEST-VOLUME conversion event.
 *
 * Many pixels fire Purchase, Lead and CompleteRegistration on the same checkout, so the three counts
 * land within a couple of percent of each other and a pure volume ranking picks whichever is
 * fractionally largest. Measured on prod (Meta, 2026-07-03 → 2026-08-31, n=1,181 purchase-reporting
 * accounts): volume ranking keeps only 68.1% of them on purchases. One live account reported 19,578
 * leads, 19,031 registrations and 18,776 purchases — and a €2.07M purchase value against €759k of
 * lead value, so ranking by count showed 0.68x on a business returning 1.84x. It is not even stable
 * across windows: the same account's "winner" changes month to month, so the date range would change
 * what the ROAS column MEANS.
 *
 * 5% keeps 91.2% of purchase-reporting accounts on purchases while still routing away the accounts
 * whose purchase event is incidental (the 104 it rejects have a median purchase share of 1.16%).
 */
exports.PURCHASE_MATERIAL_COUNT_SHARE = 0.05;
/**
 * The share of window spend on purchase-optimised ad sets that ALSO makes the purchase material.
 *
 * The OR with `PURCHASE_MATERIAL_COUNT_SHARE` is load-bearing in both directions, and each leg
 * catches accounts the other misses. Measured on the same population: the count leg alone keeps
 * 91.2%, and adding this leg keeps 93.6% — 28 accounts put a fifth or more of their budget into
 * purchase-optimised ad sets while their purchase count is swamped by a co-firing event. In the
 * other direction, an account whose ad sets optimise for a named custom conversion has a purchase
 * spend share of 0 and is kept only by its count share.
 */
exports.PURCHASE_MATERIAL_SPEND_SHARE = 0.2;
/**
 * The landing-page-view rate below which an account's arrivals are not a usable denominator.
 *
 * `landing_page_view` is derived from the browser pixel's `PageView`, so on a site whose pixel is
 * consent-gated it under-counts arrivals STRUCTURALLY rather than proportionally — every rate built
 * on it is then wrong in a way that still looks plausible. The floor is the 10th percentile of the
 * measured base (Meta, `7d_click_1d_view`, 2026-07-01 → 2026-08-31, 1381 accounts with ≥1000 link
 * clicks: median 73%, p25 47.5%, p10 11%).
 *
 * p10 rather than p25 on purpose: p25 would demote a denominator that is merely mediocre, while p10
 * selects the accounts whose measurement is broken. It is the SECOND of two credibility tests — the
 * first, a terminal conversion count exceeding the arrivals that must precede it, is exact but only
 * fires on high-converting accounts; this one catches the consent-gated accounts that convert too
 * thinly to trip it.
 */
exports.LANDING_PAGE_VIEW_RATE_CREDIBLE_FLOOR = 0.11;
/**
 * The checkout rate below which an account's CHECKOUT STARTS are not a usable denominator.
 *
 * The mirror of `LANDING_PAGE_VIEW_RATE_CREDIBLE_FLOOR`, for a different failure. `InitiateCheckout`
 * fires from the checkout page, so a store whose express checkout (Shop Pay, Apple Pay, PayPal from
 * the cart drawer) never renders one loses the event for every buyer who takes that path — while the
 * Purchase still fires, usually server-side. The checkout count is then a fraction of the truth and
 * every rate reading it is wrong in a way that still looks plausible.
 *
 * The denominator is ADDS TO CART — the step immediately above it on the purchase path, so the floor
 * measures the pair the funnel actually renders. Not landing-page views, which sit one step further up
 * and are themselves condemned on a consent-gated account; a floor placed there would inherit the
 * arrival step's brokenness and condemn the wrong row.
 *
 * The floor is the 10th percentile of the measured base (prod ClickHouse, Meta, 30 days to
 * 2026-09-02, 681 accounts with >100 adds to cart and non-zero checkouts and purchases: median 42.4%,
 * p25 27.5%, p10 18.6%). p10 rather than p25 for the same reason as its sibling: p25 demotes a
 * denominator that is merely mediocre, p10 selects the accounts whose measurement is broken.
 *
 * It is the SECOND of two credibility tests. The first — a conversion count exceeding the checkouts
 * that must precede it — is exact and needs no denominator, but fires only where the account converts
 * well enough to overtake (4.8% of the base); this one catches the rest (12.8% trip either).
 */
exports.CHECKOUT_RATE_CREDIBLE_FLOOR = 0.186;
/**
 * The step-through rates whose numerator and denominator count DIFFERENT populations, and can
 * therefore legitimately exceed 100%.
 *
 * `omni_landing_page_view` is Meta's cross-surface de-duplicated LPV (web + on-Meta + app), while
 * `outbound_clicks` counts only clicks that leave Meta — so an ad with an on-Meta destination can
 * report more arrivals than exits. `checkoutCompletionRate` has the mirror exposure: a purchase can
 * be attributed without a recorded `omni_initiated_checkout`.
 *
 * Above 1.0 these are not step-through shares at all, so a band must be withheld rather than placing
 * the value in its top edge and calling it "healthy".
 */
/**
 * The bands whose numbers are calibrated on PURCHASE-driven behaviour, and which therefore judge only
 * an account whose outcome is a purchase.
 *
 * `roas`'s edges descend from `ECOM_ANCHORS.roasRatio = 1.9`, a purchase-derived median, and
 * `checkoutCompletionRate` describes a cart checkout. Applying either to an account whose outcome is
 * a trial or a booked call grades one population with another's yardstick — so on those accounts the
 * band is STATED and the verdict withheld, the same treatment a currency mismatch already gets.
 *
 * Lives here rather than beside one surface's evaluator because both the detail panel's chips and the
 * grid's cell tones have to make the same call; two copies is how they would come to disagree.
 */
/**
 * How many rows a built-in view's adaptive count floor aims to keep.
 *
 * A "top creatives" list needs an N, and this is it. The floor a view applies is
 * `min(its house floor, the Nth-ranked creative's own count)` — so on an account whose Nth creative
 * clears the house floor the floor is unchanged, and on one whose whole distribution sits below it
 * the floor falls just far enough to leave a ranking to read.
 *
 * Ten because that is the length of list a buyer scans before acting. Lower and the view stops being
 * a ranking; higher and it admits counts too small to order by on a thin account.
 */
exports.ADAPTIVE_FLOOR_TARGET_ROWS = 10;
exports.PURCHASE_CALIBRATED_BAND_KEYS = ['checkoutCompletionRate', 'roas'];
/**
 * Whether a band may deliver a VERDICT for an account with this outcome. `undefined` means the caller
 * does not know the outcome, which reads as "do not withhold" — the pre-model behaviour.
 */
function bandJudgesOutcome(key, isPurchaseOutcome) {
    return isPurchaseOutcome === false ? !exports.PURCHASE_CALIBRATED_BAND_KEYS.includes(key) : true;
}
exports.CROSS_SURFACE_STEP_THROUGH_KEYS = [
    'landingPageViewRate',
    'checkoutCompletionRate',
];
/** The band for a key, or `undefined` when the key names no published band. */
function creativeBand(key) {
    return exports.CREATIVE_METRIC_BANDS[key];
}
/**
 * The edge a value falls in. Boundary values land in the HIGHER band (`< upTo`), so exactly `0.25`
 * on hook rate is "solid" rather than the edge below it. `null` for a `null` value — a rate with no
 * denominator has no band, and coercing it to zero would put it in the worst one.
 */
function creativeBandEdge(key, value) {
    if (value === null || value === undefined || !Number.isFinite(value))
        return null;
    const { edges } = exports.CREATIVE_METRIC_BANDS[key];
    for (const edge of edges) {
        if (edge.upTo === null || value < edge.upTo)
            return edge;
    }
    return edges[edges.length - 1] ?? null;
}
/**
 * The one-sentence fallback-anchor block the media-buying prompts state when an account is too cold
 * to benchmark. Built from `ECOM_ANCHORS` so the prompt and the UI cannot state different numbers.
 */
function ecomFallbackAnchorSentence() {
    const a = exports.ECOM_ANCHORS;
    return (`median CPA ~$${money(a.cpaMajor)} · CPM ~$${money(a.cpmMajor)} (US ~$${money(a.cpmUsMajor)}, EU ~$${money(a.cpmEuMajor)}) · ` +
        `CTR ~${pct(a.ctrFraction)}% · ROAS ~${a.roasRatio}x · store CVR ~${pct(a.storeSessionCvrFraction)}%`);
}
/**
 * The video-diagnostic band sentence the media-buying prompts state alongside the anchors. Read off
 * `VIDEO_RATE_THRESHOLDS`, so a moved threshold moves both surfaces in one edit.
 */
function videoBandSentence() {
    const { hook, hold, hookToClick } = exports.VIDEO_RATE_THRESHOLDS;
    return (`Video hook rate: <${pct(hook.weakBelow)}% weak · ${pct(hook.solidFrom)}–${pct(hook.solidTo)}% solid · >${pct(hook.strongFrom)}% strong; ` +
        `hold ${pct(hold.okFrom)}–${pct(hold.okTo)}% ok · >${pct(hold.strongFrom)}% strong; ` +
        `hook-to-click <${pct(hookToClick.weakBelow)}% weak · >${pct(hookToClick.strongFrom)}% strong`);
}
/**
 * View-through share: the conversions the provider credited on a VIEW window, over the total count
 * of the SAME event.
 *
 * ONE definition, in the shared package, because the backend computes this for filter and sort while
 * the frontend computes it for display — and two implementations of one rate is exactly the drift
 * that put `holdRate` on two different denominators until a colocated agreement test caught it.
 *
 * Three outcomes, and the distinction between the first two is the whole reason the metric exists:
 *
 *   `null`  the provider reported NO window sub-values — the split is unknown. The surface renders
 *           an em dash. It must NOT render `0%`, which asserts that every conversion was clicked,
 *           a confident claim made from no evidence. 27% of sampled action entries land here.
 *   `0`     the split WAS measured and the view window was credited nothing. A real zero.
 *   `n`     the measured share.
 *
 * `total` is the count the surface shows beside it (`results`), not a separately derived figure —
 * a share whose denominator differs from the number printed next to it is a share nobody can check.
 * That assumes the account's attribution setting is the one we request (`7d_click`/`1d_view`), so
 * the returned windows partition the total; every production row sampled so far agrees. An account
 * measured on a WIDER setting would have a total that exceeds its two windows and would read low.
 * The row carries `attributionSetting` beside this, which is where to look if a figure surprises.
 */
function viewThroughShare(viewConversions, total, splitMeasured) {
    if (!splitMeasured)
        return null;
    const view = Number(viewConversions ?? 0);
    const denominator = Number(total ?? 0);
    if (!Number.isFinite(view) || !Number.isFinite(denominator) || denominator <= 0)
        return null;
    return view / denominator;
}
/**
 * The view-through-share reading the media-buying prompts state. Same source as the creative panel's
 * attribution banner, so "what counts as a heavy view-through account" has one answer.
 */
function viewThroughShareSentence() {
    const t = exports.VIEW_THROUGH_THRESHOLDS;
    return (`~${t.normalFrom.toFixed(2)}–${t.normalTo.toFixed(2)} is normal, >${t.reviewFrom.toFixed(2)} worth review, ` +
        `>${t.overClaimingFrom.toFixed(2)} means the platform is effectively claiming sales it didn't drive`);
}
